import { computed } from 'vue'

export function useTrainingModeSetup({
  t,
  isMobile,
  isExamMode,
  isMistakeReviewMode,
  isAssignedTestMode,
  selectedTopics,
  assignedTestsLoading,
  hasAssignedTests,
  currentAssignedTest,
  incorrectQuestionsCount,
  incorrectQuestionsLoading,
  mistakeReviewCountPreset,
  mistakeReviewCountOptions,
  resolvedMistakeQuestionCount
}) {
  const trainingVariant = computed({
    get: () => {
      if (isAssignedTestMode.value) return 'assigned'
      return isMistakeReviewMode.value ? 'mistakes' : 'standard'
    },
    set: (value) => {
      isMistakeReviewMode.value = value === 'mistakes'
      isAssignedTestMode.value = value === 'assigned'
    }
  })

  const modeCardState = computed(() => {
    if (isAssignedTestMode.value) {
      return {
        icon: 'mdi-clipboard-text-play-outline',
        color: 'info',
        title: t('test.assignedTests'),
        description: t('test.assignedTestsHint')
      }
    }

    if (isMistakeReviewMode.value) {
      return {
        icon: 'mdi-refresh-auto',
        color: 'warning',
        title: t('test.mistakeReview'),
        description: t('test.mistakeReviewDesc')
      }
    }

    if (isExamMode.value) {
      return {
        icon: 'mdi-certificate',
        color: 'warning',
        title: t('test.modes.exam'),
        description: t('test.modes.examDesc')
      }
    }

    return {
      icon: 'mdi-school',
      color: 'primary',
      title: t('test.modes.training'),
      description: t('test.modes.trainingDesc')
    }
  })

  const showTopicsSelector = computed(() => (
    !isExamMode.value && !isMistakeReviewMode.value && !isAssignedTestMode.value
  ))
  const showExamTopicsInfo = computed(() => (
    isExamMode.value && !isMistakeReviewMode.value && !isAssignedTestMode.value
  ))
  const showTopicsHint = computed(() => !isMobile.value)
  const compactMistakeModeCard = computed(() => isMobile.value && isMistakeReviewMode.value)
  const inlineStartBarOnMobile = computed(() => (
    isMobile.value && (isMistakeReviewMode.value || isAssignedTestMode.value)
  ))
  const mistakeReviewToggleLabel = computed(() => (
    isMobile.value ? t('test.mistakeReviewShort') : t('test.mistakeReview')
  ))
  const assignedTrainingToggleLabel = computed(() => (
    isMobile.value ? t('test.assignedTestShort') : t('test.assignedTests')
  ))
  const mistakeReviewCountHeading = computed(() => (
    isMobile.value ? t('test.mistakeReviewCountLabelShort') : t('test.mistakeReviewCountLabel')
  ))
  const startButtonIcon = computed(() => (
    isAssignedTestMode.value
      ? 'mdi-clipboard-text-play-outline'
      : isMistakeReviewMode.value
        ? 'mdi-refresh'
        : 'mdi-play'
  ))
  const startButtonLabel = computed(() => (
    isAssignedTestMode.value
      ? (isMobile.value ? t('test.startAssignedTestShort') : t('test.startAssignedTest'))
      : isMistakeReviewMode.value
        ? (isMobile.value ? t('test.startMistakeReviewShort') : t('test.startMistakeReview'))
        : t('test.startTest')
  ))

  const canStartConfiguredMode = computed(() => {
    if (isAssignedTestMode.value) {
      if (assignedTestsLoading.value) return false
      return !!currentAssignedTest.value
    }

    if (isMistakeReviewMode.value) {
      if (incorrectQuestionsLoading.value) return false
      return incorrectQuestionsCount.value > 0 && resolvedMistakeQuestionCount.value > 0
    }

    return selectedTopics.value.length > 0
  })

  return {
    trainingVariant,
    modeCardState,
    showTopicsSelector,
    showExamTopicsInfo,
    showTopicsHint,
    compactMistakeModeCard,
    inlineStartBarOnMobile,
    mistakeReviewToggleLabel,
    assignedTrainingToggleLabel,
    mistakeReviewCountHeading,
    mistakeReviewCountPreset,
    mistakeReviewCountOptions,
    resolvedMistakeQuestionCount,
    startButtonIcon,
    startButtonLabel,
    canStartConfiguredMode,
    hasAssignedTests
  }
}
